import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { api, useAuth } from '../context/AuthContext';
import { ScanPageCard } from '../components/scan/ScanPageCard';
import { TeamDokZuordnung, refFromSel } from '../components/register/TeamDokZuordnung';
import {
  ArrowLeft, Camera, Images, FileText, Loader2, CheckCircle2, Download, AlertCircle, ScanLine, Sparkles,
} from 'lucide-react';

const MAX_PAGES = 20;
const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];
const COLOR_MODES = [
  { key: 'auto', labelKey: 'scan.color_mode.auto' },
  { key: 'color', labelKey: 'scan.color_mode.color' },
  { key: 'gray', labelKey: 'scan.color_mode.gray' },
  { key: 'bw', labelKey: 'scan.color_mode.bw' },
];
const LS_REF = 'scan_mandate_ref';

let _uid = 0;
const uid = () => `p${Date.now()}_${_uid++}`;

const formatSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const DokumentScan = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [pages, setPages] = useState([]);
  const { user } = useAuth();
  const isTeam = ['admin', 'kanzlei_mitarbeiter'].includes(user?.role);
  const [regSel, setRegSel] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [docType, setDocType] = useState('');
  const [mandateRef, setMandateRef] = useState(() => localStorage.getItem(LS_REF) || '');
  const [description, setDescription] = useState('');
  const [colorMode, setColorMode] = useState('auto');
  const [ocr, setOcr] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/documents/scan/types').then((r) => setDocTypes(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => () => pages.forEach((p) => URL.revokeObjectURL(p.originalUrl)), []); // eslint-disable-line

  const pagesRef = useRef(pages);
  useEffect(() => { pagesRef.current = pages; }, [pages]);

  const requestPreview = useCallback(async (page, mode) => {
    const fd = new FormData();
    fd.append('file', page.file);
    fd.append('rotate', String(page.rotate));
    fd.append('crop', String(page.crop));
    fd.append('color_mode', mode);
    const apply = (patch) => setPages((prev) => prev.map((p) => (p.id === page.id && p.seq === page.seq ? { ...p, ...patch } : p)));
    try {
      const { data } = await api.post('/documents/scan/preview', fd);
      apply({ preview: data.image, cropped: data.cropped, loading: false, error: '' });
    } catch (e) {
      apply({ loading: false, error: e.response?.data?.detail || t('scan.error.preview_failed') });
    }
  }, [t]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      return IMAGE_EXT.includes(ext) || f.type.startsWith('image/');
    });
    if (!files.length) return;
    setError('');
    setResult(null);
    const room = MAX_PAGES - pages.length;
    if (files.length > room) {
      toast({ title: t('scan.toast.max_pages_title', { count: MAX_PAGES }), description: t('scan.toast.max_pages_description', { count: files.length - room }) });
    }
    const newPages = files.slice(0, room).map((file) => ({
      id: uid(), seq: 0, file, originalUrl: URL.createObjectURL(file), preview: null, cropped: false, rotate: 0, crop: true, loading: true, error: '',
    }));
    setPages((prev) => [...prev, ...newPages]);
    newPages.forEach((p) => requestPreview(p, colorMode));
  };

  const updatePage = (id, patch) => {
    const cur = pagesRef.current.find((p) => p.id === id);
    if (!cur) return;
    const page = { ...cur, ...patch, seq: cur.seq + 1, loading: true };
    setPages((prev) => prev.map((p) => (p.id === id ? page : p)));
    requestPreview(page, colorMode);
  };

  const changeColorMode = (mode) => {
    setColorMode(mode);
    const next = pagesRef.current.map((p) => ({ ...p, seq: p.seq + 1, loading: true }));
    setPages(next);
    next.forEach((p) => requestPreview(p, mode));
  };

  const movePage = (index, dir) => {
    setPages((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const removePage = (id) => setPages((prev) => {
    const p = prev.find((x) => x.id === id);
    if (p) URL.revokeObjectURL(p.originalUrl);
    return prev.filter((x) => x.id !== id);
  });

  const busy = submitting || pages.some((p) => p.loading);
  const canSubmit = pages.length > 0 && !busy && docType && (isTeam ? !!regSel?.mandanten_nr : mandateRef.trim()) && !pages.some((p) => p.error);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    setProgress(0);
    const fd = new FormData();
    pages.forEach((p) => fd.append('files', p.file));
    fd.append('mandate_ref', mandateRef.trim());
    if (isTeam && regSel?.mandanten_nr) { fd.append('register_mnr', regSel.mandanten_nr); fd.append('register_case_id', regSel.case_id || ''); }
    fd.append('doc_type', docType);
    fd.append('color_mode', colorMode);
    fd.append('ocr', String(ocr));
    fd.append('page_options', JSON.stringify(pages.map((p) => ({ rotate: p.rotate, crop: p.crop }))));
    if (description.trim()) fd.append('description', description.trim());
    try {
      const { data } = await api.post('/documents/scan', fd, {
        timeout: 300000,
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      localStorage.setItem(LS_REF, mandateRef.trim());
      setResult(data);
      pages.forEach((p) => URL.revokeObjectURL(p.originalUrl));
      setPages([]);
      toast({ title: t('scan.toast.pdf_created_title'), description: t('scan.toast.pdf_created_description', { filename: data.filename }) });
    } catch (e) {
      setError(e.response?.data?.detail || t('scan.error.submit_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    const response = await api.get(`/documents/${result.doc_id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = result.filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const inputCls = 'w-full px-4 py-2.5 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';

  return (
    <div className="min-h-screen bg-white">
      <Seo title={t('scan.seo.title')} description={t('scan.seo.description')} path="/dokumente/scannen" />
      <Header />
      <main className="pt-[150px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Link to="/dokumente" className="inline-flex items-center gap-1 text-[#34536b] text-sm hover:text-[#b8893d] mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('scan.back_link')}
            </Link>
            <div className="mb-10">
              <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('scan.hero.eyebrow')}</p>
              <h1 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{t('scan.hero.title')}</h1>
              <p className="text-[#34536b] text-sm md:text-base mt-3 max-w-2xl">
                {t('scan.hero.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Linke Spalte: Fotos */}
              <div className="lg:col-span-2 space-y-6">
                <div
                  onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                  onDragOver={(e) => e.preventDefault()}
                  className="bg-white border-2 border-dashed border-gray-300 hover:border-[#b8893d] transition-colors rounded-sm p-8 text-center"
                  data-testid="scan-dropzone"
                >
                  <ScanLine className="w-10 h-10 text-[#b8893d] mx-auto mb-3" />
                  <p className="text-[#1B3A5C] font-medium">{t('scan.dropzone.title')}</p>
                  <p className="text-[#34536b] text-xs mt-1 mb-5">{t('scan.dropzone.hint', { maxPages: MAX_PAGES })}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button type="button" onClick={() => cameraRef.current?.click()} disabled={submitting} className="bg-[#1B3A5C] hover:bg-[#15304c] text-white tracking-wider text-xs" data-testid="scan-camera-btn">
                      <Camera className="mr-2 w-4 h-4" /> {t('scan.dropzone.camera_button')}
                    </Button>
                    <Button type="button" onClick={() => galleryRef.current?.click()} disabled={submitting} variant="outline" className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs" data-testid="scan-gallery-btn">
                      <Images className="mr-2 w-4 h-4" /> {t('scan.dropzone.gallery_button')}
                    </Button>
                  </div>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="scan-camera-input" />
                  <input ref={galleryRef} type="file" accept={`image/*,${IMAGE_EXT.join(',')}`} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="scan-gallery-input" />
                </div>

                {pages.length > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-[#1B3A5C] text-sm font-medium" data-testid="scan-page-count">
                        {t('scan.pages.count', { count: pages.length })} · {formatSize(pages.reduce((s, p) => s + p.file.size, 0))}
                      </p>
                      <p className="text-[#34536b] text-xs">{t('scan.pages.reorder_hint')}</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="scan-pages-grid">
                      {pages.map((p, i) => (
                        <ScanPageCard
                          key={p.id}
                          page={p}
                          index={i}
                          total={pages.length}
                          busy={submitting}
                          onRotate={() => updatePage(p.id, { rotate: (p.rotate + 90) % 360 })}
                          onToggleCrop={() => updatePage(p.id, { crop: !p.crop })}
                          onMove={(dir) => movePage(i, dir)}
                          onRemove={() => removePage(p.id)}
                        />
                      ))}
                    </div>
                  </>
                )}

                {result && (
                  <div className="bg-white border-l-4 border-emerald-500 rounded-sm p-6 shadow-sm" data-testid="scan-result">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-xl text-[#1B3A5C]">{t('scan.result.title')}</h3>
                        <p className="text-[#34536b] text-sm mt-1 break-all">
                          <FileText className="inline w-4 h-4 mr-1 -mt-0.5" />{result.filename}
                        </p>
                        <p className="text-[#34536b] text-xs mt-1">
                          {t('scan.result.pages', { count: result.pages })} · {formatSize(result.size)}
                          {result.ocr_words > 0 && ` · ${t('scan.result.ocr_info', { count: result.ocr_words })}`}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-4">
                          <Button type="button" onClick={handleDownload} className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs" data-testid="scan-result-download">
                            <Download className="mr-2 w-4 h-4" /> {t('scan.result.download_button')}
                          </Button>
                          <Link to="/dokumente">
                            <Button type="button" variant="outline" className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs" data-testid="scan-result-docs-link">
                              {t('scan.result.docs_button')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rechte Spalte: Einstellungen */}
              <aside className="lg:sticky lg:top-[170px] self-start bg-white rounded-sm shadow-sm border border-gray-100">
                <div className="bg-[#1B3A5C] text-white px-6 py-4">
                  <h2 className="font-serif text-lg md:text-xl">{t('scan.form.title')}</h2>
                </div>
                <div className="p-6 space-y-4">
                  {error && (
                    <div className="flex items-start gap-2 p-3 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-sm" data-testid="scan-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] text-[#34536b] mb-1.5">{t('scan.form.doctype_label')}</label>
                    <select value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls} data-testid="scan-doctype-select">
                      <option value="">{t('scan.form.doctype_placeholder')}</option>
                      {docTypes.map((type) => <option key={type.key} value={type.key}>{type.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] text-[#34536b] mb-1.5">{t('scan.form.mandate_ref_label')}</label>
                    {isTeam ? <TeamDokZuordnung value={regSel} onChange={(v) => { setRegSel(v); setMandateRef(refFromSel(v)); }} testId="scan-zuordnung" />
                      : <input value={mandateRef} onChange={(e) => setMandateRef(e.target.value)} placeholder={t('scan.form.mandate_ref_placeholder')} className={inputCls} data-testid="scan-mandate-ref" />}
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] text-[#34536b] mb-1.5">{t('scan.form.description_label')}</label>
                    <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('scan.form.description_placeholder')} className={`${inputCls} resize-none`} data-testid="scan-description" />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] text-[#34536b] mb-1.5">{t('scan.form.color_mode_label')}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {COLOR_MODES.map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => changeColorMode(m.key)}
                          disabled={busy}
                          data-testid={`scan-mode-${m.key}`}
                          className={`px-3 py-2 text-xs rounded-sm border transition-colors ${colorMode === m.key ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}
                        >
                          {t(m.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={ocr} onChange={(e) => setOcr(e.target.checked)} className="mt-0.5 accent-[#b8893d]" data-testid="scan-ocr-checkbox" />
                    <span className="text-sm text-[#1B3A5C]">
                      <Sparkles className="inline w-3.5 h-3.5 text-[#b8893d] mr-1 -mt-0.5" />{t('scan.form.ocr_title')}
                      <span className="block text-xs text-[#34536b] mt-0.5">{t('scan.form.ocr_description')}</span>
                    </span>
                  </label>

                  {submitting && (
                    <div data-testid="scan-progress">
                      <div className="flex justify-between text-xs text-[#34536b] mb-1">
                        <span>{progress < 100 ? t('scan.progress.uploading') : t('scan.progress.creating')}</span>
                        <span>{progress} %</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full bg-[#b8893d] transition-all ${progress >= 100 ? 'animate-pulse' : ''}`} style={{ width: `${Math.max(progress, 4)}%` }} />
                      </div>
                    </div>
                  )}

                  <Button type="button" onClick={handleSubmit} disabled={!canSubmit} className="w-full bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs disabled:opacity-40" data-testid="scan-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="mr-2 w-4 h-4" /> {t('scan.form.submit_button')}</>}
                  </Button>
                  <p className="text-[11px] text-[#34536b] leading-relaxed">
                    {t('scan.form.privacy_notice')}
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default DokumentScan;
